/**
 * Dashboard Analytics Service
 * Hybrid approach with real-time metrics and optimized batch analytics
 */

import axios from 'axios';

// Create a simple HTTP client for dashboard API calls
const httpClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'https://6oi3xs2kzb.execute-api.ap-southeast-1.amazonaws.com',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
httpClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface DateRange {
  start_date?: string;
  end_date?: string;
  days?: number;
}

export interface DashboardOverview {
  total_requirements: number;
  active_requirements: number;
  inactive_requirements: number;
  total_submissions: number;
  avg_fitment_score: number;
  last_updated: string;
}

export interface ClientBreakdown {
  client_id: string;
  client_name: string;
  requirements: number;
  positions: number;
  submissions: number;
  avg_fitment_score: number;
}

export interface FitmentScoreDistribution {
  range: string;
  count: number;
  percentage: number;
}

export interface RecruiterPerformance {
  recruiter_id: string;
  recruiter_name: string;
  manager_name: string;
  total_submissions: number;
  fitment_scores: {
    '90-100': number;
    '80-90': number;
    '70-80': number;
    '60-70': number;
    '50-60': number;
    '40-50': number;
    '<40': number;
  };
}

export interface TimeSeriesData {
  date: string;
  submissions: number;
  requirements: number;
}

export interface RequirementAnalytics {
  requirement_id: string;
  requirement: string;
  client: string;
  status: string;
  submissions: number;
  avg_score: number;
  created_date: string;
  last_submission: string;
}

export interface ManagerPerformance {
  manager: string;
  submissions: number;
  recruiter_count: number;
  recruiters: string[];
}

export interface PipelineHealth {
  submitted: number;
  reviewing: number;
  shortlisted: number;
  rejected: number;
  withdrawn: number;
  total: number;
}

export interface Alert {
  id: string;
  type: 'at_risk_requirement' | 'high_fitment_uncontacted' | 'bottleneck' | 'stale_candidate';
  title: string;
  description: string;
  severity: 'high' | 'medium' | 'low';
  actionable: boolean;
  link_url?: string;
  metadata?: Record<string, any>;
}

export interface QualityMetrics {
  avg_fitment_score: number;
  score_trend: number;
  high_quality_rate: number;
  common_skill_gaps: Array<{ skill: string; count: number }>;
  score_vs_outcome_correlation: {
    shortlisted_avg: number;
    rejected_avg: number;
  };
}

export interface PeriodComparison {
  current_period: {
    total_submissions: number;
    active_requirements: number;
    avg_fitment_score: number;
    shortlisted: number;
  };
  previous_period: {
    total_submissions: number;
    active_requirements: number;
    avg_fitment_score: number;
    shortlisted: number;
  };
  percentage_change: {
    total_submissions: number;
    active_requirements: number;
    avg_fitment_score: number;
    shortlisted: number;
  };
  trend_direction: {
    total_submissions: 'up' | 'down' | 'stable';
    active_requirements: 'up' | 'down' | 'stable';
    avg_fitment_score: 'up' | 'down' | 'stable';
    shortlisted: 'up' | 'down' | 'stable';
  };
}

export interface ComprehensiveAnalytics {
  overview: DashboardOverview;
  client_breakdown: ClientBreakdown[];
  fitment_score_distribution: FitmentScoreDistribution[];
  recruiter_performance: RecruiterPerformance[];
  time_series: TimeSeriesData[];
  requirements_analytics: RequirementAnalytics[];
  manager_performance: ManagerPerformance[];
  pipeline_health: PipelineHealth;
  alerts: Alert[];
  quality_metrics: QualityMetrics;
  period_comparison: PeriodComparison;
}

export interface AIInsights {
  summary: string;
  trends: string[];
  recommendations: string[];
}

export interface AggregatedDashboardData {
  overview: DashboardOverview;
  ai_insights: AIInsights;
  date?: string;
  ai_generated_at?: string;
  source: 'aggregated' | 'realtime';
}

class DashboardService {

  /**
   * Get real-time dashboard overview metrics
   * Cache: 1 minute for real-time updates
   */
  async getOverview(dateRange?: DateRange): Promise<DashboardOverview> {
    const params = new URLSearchParams();
    if (dateRange?.start_date) params.append('start_date', dateRange.start_date);
    if (dateRange?.end_date) params.append('end_date', dateRange.end_date);
    if (dateRange?.days) params.append('days', dateRange.days.toString());

    const response = await httpClient.get(`/api/v1/dashboard/analytics/overview?${params}`);
    return response.data.data;
  }

  /**
   * Get client-wise analytics breakdown
   * Cache: 5 minutes for complex aggregations
   */
  async getClientBreakdown(dateRange?: DateRange): Promise<ClientBreakdown[]> {
    const params = new URLSearchParams();
    if (dateRange?.start_date) params.append('start_date', dateRange.start_date);
    if (dateRange?.end_date) params.append('end_date', dateRange.end_date);
    if (dateRange?.days) params.append('days', dateRange.days.toString());

    const response = await httpClient.get(`/api/v1/dashboard/analytics/client-breakdown?${params}`);
    return response.data.data;
  }

  /**
   * Get fitment score distribution analytics
   * Cache: 5 minutes for aggregations
   */
  async getFitmentScoreDistribution(dateRange?: DateRange): Promise<FitmentScoreDistribution[]> {
    const params = new URLSearchParams();
    if (dateRange?.start_date) params.append('start_date', dateRange.start_date);
    if (dateRange?.end_date) params.append('end_date', dateRange.end_date);
    if (dateRange?.days) params.append('days', dateRange.days.toString());

    const response = await httpClient.get(`/api/v1/dashboard/analytics/fitment-distribution?${params}`);
    return response.data.data;
  }

  /**
   * Get recruiter performance analytics with fitment score breakdown
   * Cache: 5 minutes for complex aggregations
   */
  async getRecruiterPerformance(dateRange?: DateRange): Promise<RecruiterPerformance[]> {
    const params = new URLSearchParams();
    if (dateRange?.start_date) params.append('start_date', dateRange.start_date);
    if (dateRange?.end_date) params.append('end_date', dateRange.end_date);
    if (dateRange?.days) params.append('days', dateRange.days.toString());

    const response = await httpClient.get(`/api/v1/dashboard/analytics/recruiter-performance?${params}`);
    return response.data.data;
  }

  /**
   * Get time series analytics (day-wise submissions)
   * Cache: 5 minutes for time series data
   */
  async getTimeSeriesData(dateRange?: DateRange): Promise<TimeSeriesData[]> {
    const params = new URLSearchParams();
    if (dateRange?.start_date) params.append('start_date', dateRange.start_date);
    if (dateRange?.end_date) params.append('end_date', dateRange.end_date);
    if (dateRange?.days) params.append('days', dateRange.days.toString());

    const response = await httpClient.get(`/api/v1/dashboard/analytics/time-series?${params}`);
    return response.data.data;
  }

  /**
   * Get requirement-wise analytics with submissions and scores
   * Cache: 5 minutes for complex aggregations
   */
  async getRequirementsAnalytics(dateRange?: DateRange): Promise<RequirementAnalytics[]> {
    const params = new URLSearchParams();
    if (dateRange?.start_date) params.append('start_date', dateRange.start_date);
    if (dateRange?.end_date) params.append('end_date', dateRange.end_date);
    if (dateRange?.days) params.append('days', dateRange.days.toString());

    const response = await httpClient.get(`/api/v1/dashboard/analytics/requirements?${params}`);
    return response.data.data;
  }

  /**
   * Get manager-wise performance analytics
   * Cache: 5 minutes for aggregations
   */
  async getManagerPerformance(dateRange?: DateRange): Promise<ManagerPerformance[]> {
    const params = new URLSearchParams();
    if (dateRange?.start_date) params.append('start_date', dateRange.start_date);
    if (dateRange?.end_date) params.append('end_date', dateRange.end_date);
    if (dateRange?.days) params.append('days', dateRange.days.toString());

    const response = await httpClient.get(`/api/v1/dashboard/analytics/manager-performance?${params}`);
    return response.data.data;
  }

  /**
   * Get comprehensive dashboard analytics in a single request
   * Combines multiple data points with optimized caching
   */
  async getComprehensiveAnalytics(dateRange?: DateRange): Promise<ComprehensiveAnalytics> {
    const params = new URLSearchParams();
    if (dateRange?.start_date) {
      // Ensure date is in ISO format (YYYY-MM-DD)
      const startDate = dateRange.start_date.includes('T') 
        ? dateRange.start_date.split('T')[0] 
        : dateRange.start_date;
      params.append('start_date', startDate);
    }
    if (dateRange?.end_date) {
      // Ensure date is in ISO format (YYYY-MM-DD)
      const endDate = dateRange.end_date.includes('T') 
        ? dateRange.end_date.split('T')[0] 
        : dateRange.end_date;
      params.append('end_date', endDate);
    }
    if (dateRange?.days) params.append('days', dateRange.days.toString());

    const response = await httpClient.get(`/api/v1/dashboard/analytics/comprehensive?${params}`);
    return response.data.data;
  }

  /**
   * Clear dashboard cache (admin only)
   */
  async clearCache(): Promise<void> {
    await httpClient.post(`/api/v1/dashboard/cache/clear`);
  }

  /**
   * Get aggregated dashboard data with AI insights
   * Returns pre-aggregated metrics from dashboard_summary table with AI insights
   */
  async getAggregatedDashboard(dateRange?: DateRange): Promise<AggregatedDashboardData> {
    const params = new URLSearchParams();
    if (dateRange?.start_date) {
      const startDate = dateRange.start_date.includes('T') 
        ? dateRange.start_date.split('T')[0] 
        : dateRange.start_date;
      params.append('start_date', startDate);
    }
    if (dateRange?.end_date) {
      const endDate = dateRange.end_date.includes('T') 
        ? dateRange.end_date.split('T')[0] 
        : dateRange.end_date;
      params.append('end_date', endDate);
    }
    if (dateRange?.days) params.append('days', dateRange.days.toString());

    const response = await httpClient.get(`/api/v1/dashboard/analytics/aggregated?${params}`);
    return response.data.data;
  }

  /**
   * Get dashboard cache statistics (admin only)
   */
  async getCacheStats(): Promise<any> {
    const response = await httpClient.get(`/api/v1/dashboard/cache/stats`);
    return response.data.data;
  }

  /**
   * Create date range for last N days
   */
  createDateRangeForDays(days: number): DateRange {
    return { days };
  }

  /**
   * Create date range for specific dates
   */
  createDateRange(startDate: string, endDate: string): DateRange {
    return {
      start_date: startDate,
      end_date: endDate
    };
  }

  /**
   * Get default date range (last 30 days)
   */
  getDefaultDateRange(): DateRange {
    return { days: 30 };
  }
}

export const dashboardService = new DashboardService();
export default dashboardService;
